import type { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import type { ResultOf } from '~publish/graphql'

import { SavedViewsQuery } from '../graphql/SavedViewsQuery'

type SavedViewsResponse = ResultOf<typeof SavedViewsQuery>['savedViews']

export type SavedView = SavedViewsResponse['savedViews'][number]

type UseSavedViewsQueryReturn = {
  savedViews: SavedView[]
  loading: boolean
  error: ErrorLike | null
}

const EMPTY_VIEWS: SavedView[] = []

/**
 * Fetch the current organization's saved views.
 */
export function useSavedViewsQuery(): UseSavedViewsQueryReturn {
  const organizationId = useCurrentOrganization()?.id

  const { data, loading, error } = useQuery(SavedViewsQuery, {
    variables: {
      input: { organizationId: organizationId ?? '' },
    },
    skip: !organizationId,
    fetchPolicy: 'cache-first',
  })

  if (!organizationId) {
    return { savedViews: EMPTY_VIEWS, loading: true, error: null }
  }

  // Data-first: mutations refetch this query, and surfacing that in-flight
  // window as loading would drop the whole sidebar section to skeletons on
  // every create/rename/delete. Cached data (including optimistic layers)
  // keeps rendering; the skeleton is reserved for the cold load.
  if (data) {
    return {
      savedViews: data.savedViews.savedViews,
      loading: false,
      error: null,
    }
  }

  if (loading) {
    return { savedViews: EMPTY_VIEWS, loading: true, error: null }
  }

  return { savedViews: EMPTY_VIEWS, loading: false, error: error ?? null }
}
